import { Fragment, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../components/CustomButton';
import CustomModal from '../../common/CustomModal';
import RowActionsMenu from '../../../components/RowActionsMenu';
import { tableCellClass, tableHeaderCellClass } from '../../../components/TableCard';
import {
    useCancelCampaignMutation,
    useGetCampaignsQuery,
    useLazyGetCampaignExportQuery,
} from '../../../api/voucher/voucherApiSlice';
import { AdminVoucher, VoucherCampaign, VoucherScope } from '../../../api/voucher/voucherApiTypes';
import { buildCampaignCsv, campaignCsvFileName } from '../../../utils/campaignCsv';
import { downloadCsv } from '../../../utils/csvDownload';
import { cn } from '../../../utils/utils';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import Permission from '../../../common/permissions';
import { campaignCsvLabels } from './campaignCsvLabels';
import { onRowClick } from '../../../utils/rowClick';

// Promo-code campaigns (#2249) on the Promotional tab: ONE row per campaign —
// "83 of 250 redeemed" — instead of 250 near-identical code rows. A row expands
// to its codes. Export hands the tenant the code ↔ address list plus who
// redeemed; cancel stops every code and erases the addresses.
//
// Rendered as its own card above the single codes, which the page lists without
// campaign codes.

const headerCellClass = tableHeaderCellClass;
const cellClass = tableCellClass;

type Terms = Pick<AdminVoucher, 'discountValue' | 'discountIsPercentage' | 'currency'>;

interface Props {
    /** The full voucher list — a campaign's codes are its rows with that campaignId. */
    vouchers: AdminVoucher[];
    formatScope: (v: VoucherScope) => string;
    formatValue: (v: Terms) => string;
    formatDate: (iso: string | null) => string;
    naLabel: string;
}

const serverMessage = (err: unknown): string | null =>
    typeof err === 'object' &&
    err !== null &&
    'data' in err &&
    typeof (err as { data?: { message?: string } }).data?.message === 'string'
        ? (err as { data: { message: string } }).data.message
        : null;

const CampaignsTable = ({ vouchers, formatScope, formatValue, formatDate, naLabel }: Props) => {
    // Cancelling a campaign is a write (manage:vouchers); the export stays a read.
    const canManageVouchers = useSelector(makeHasPermissionSelector(Permission.manageVouchers()));
    const { t } = useTranslation();
    // Refetch on mount for the same reason as the code list: redemptions happen
    // in the end-user app, so nothing here would invalidate the counts.
    const { data: campaigns, isError } = useGetCampaignsQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });
    const [fetchExport] = useLazyGetCampaignExportQuery();
    const [cancelCampaign, { isLoading: isCancelling }] = useCancelCampaignMutation();
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [exportingId, setExportingId] = useState<string | null>(null);
    const [exportError, setExportError] = useState<string | null>(null);
    const [cancelling, setCancelling] = useState<VoucherCampaign | null>(null);
    const [cancelError, setCancelError] = useState<string | null>(null);

    const codesByCampaign = useMemo(() => {
        const byCode = (a: AdminVoucher, b: AdminVoucher) =>
            (a.code ?? '').localeCompare(b.code ?? '');
        const sorted = [...vouchers].sort(byCode);
        const map = new Map<string, AdminVoucher[]>();
        sorted.forEach((v) => {
            if (!v.campaignId) return;
            const codes = map.get(v.campaignId);
            if (codes) {
                codes.push(v);
            } else {
                map.set(v.campaignId, [v]);
            }
        });
        return map;
    }, [vouchers]);

    // A failed load is not "no campaigns" — say so rather than render nothing.
    if (isError) {
        return (
            <p className={'mb-4 text-sm text-foreground/60'}>{t('vouchers_codes.load_error')}</p>
        );
    }
    if (!campaigns || campaigns.length === 0) return null;

    const handleExport = async (campaign: VoucherCampaign) => {
        setExportError(null);
        setExportingId(campaign.id);
        try {
            const data = await fetchExport(campaign.id).unwrap();
            downloadCsv(
                campaignCsvFileName(data.campaignName, t('vouchers_codes.campaign.csv.file_label')),
                buildCampaignCsv(data.rows, campaignCsvLabels(t)),
            );
        } catch (err) {
            setExportError(serverMessage(err) ?? t('vouchers_codes.campaign.export_error'));
        } finally {
            setExportingId(null);
        }
    };

    const handleConfirmCancel = async () => {
        if (!cancelling) return;
        setCancelError(null);
        try {
            await cancelCampaign(cancelling.id).unwrap();
            setCancelling(null);
        } catch (err) {
            setCancelError(serverMessage(err) ?? t('vouchers_codes.campaign.cancel.error'));
        }
    };

    return (
        <>
            {exportError && <p className={'mb-2 text-sm text-red-500'}>{exportError}</p>}
            <div className={'mb-4 bg-text-contrast rounded-[5px] shadow-sm overflow-x-auto'}>
                <table className={'w-full text-sm'}>
                    <thead>
                        <tr className={'border-b border-foreground/10'}>
                            <th className={headerCellClass}>{t('vouchers_codes.campaign.name_label')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.columns.applies_to')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.columns.value')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.columns.used')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.columns.expiry')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.columns.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {campaigns.map((c) => (
                            <Fragment key={c.id}>
                                <tr
                                    className={cn(
                                        'border-b border-foreground/5 last:border-b-0'
                                        + ' cursor-pointer hover:bg-foreground/[0.02]',
                                        c.archivedAt && 'opacity-60',
                                    )}
                                    onClick={onRowClick(() => setExpandedId((cur) => (cur === c.id ? null : c.id)))}
                                >
                                    <td className={cellClass}>
                                        <span className={'font-medium text-foreground'}>{c.name}</span>
                                        <span
                                            className={
                                                'ml-2 rounded-full border border-foreground/15 px-2 py-0.5 '
                                                + 'text-xs text-foreground/60'
                                            }
                                        >
                                            {t('vouchers_codes.campaign.badge')}
                                        </span>
                                    </td>
                                    <td className={cellClass}>{formatScope(c)}</td>
                                    <td className={cellClass}>{formatValue(c)}</td>
                                    <td className={cellClass}>
                                        {t('vouchers_codes.campaign.redeemed_of', {
                                            redeemed: c.redeemedCount,
                                            issued: c.issuedCount,
                                        })}
                                    </td>
                                    <td className={cellClass}>{formatDate(c.validUntilDate)}</td>
                                    <td className={cellClass}>
                                        <div className={'flex gap-3 items-center'}>
                                            <button
                                                type={'button'}
                                                className={'text-primary hover:underline disabled:opacity-50'}
                                                disabled={exportingId === c.id}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleExport(c);
                                                }}
                                            >
                                                {t('vouchers_codes.campaign.export')}
                                            </button>
                                            {c.archivedAt && (
                                                <span className={'text-foreground/50'}>
                                                    {t('vouchers_codes.deactivated')}
                                                </span>
                                            )}
                                            {/* Export only hands out a file, so it stays a
                                                visible link; cancelling stops every code and
                                                erases the addresses, so it sits in the row's
                                                menu (design-decisions.md › Zeilenaktionen in
                                                Tabellen). */}
                                            <RowActionsMenu
                                                rowName={c.name}
                                                items={[]}
                                                destructiveItems={[{
                                                    id: 'cancel',
                                                    label: t('vouchers_codes.cancel_code.action'),
                                                    onClick: () => {
                                                        setCancelError(null);
                                                        setCancelling(c);
                                                    },
                                                    hidden: !!c.archivedAt || !canManageVouchers,
                                                }]}
                                            />
                                        </div>
                                    </td>
                                </tr>
                                {expandedId === c.id && (
                                    <tr className={'border-b border-foreground/5 last:border-b-0'}>
                                        <td colSpan={6} className={'bg-foreground/[0.02] px-4 py-3'}>
                                            <table className={'w-full text-sm'}>
                                                <thead>
                                                    <tr className={'border-b border-foreground/5'}>
                                                        <th className={headerCellClass}>{t('vouchers_codes.columns.code')}</th>
                                                        <th className={headerCellClass}>{t('vouchers_codes.campaign.recipient')}</th>
                                                        <th className={headerCellClass}>{t('vouchers_codes.columns.used')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(codesByCampaign.get(c.id) ?? []).map((v) => (
                                                        <tr key={v.id} className={'border-b border-foreground/5 last:border-b-0'}>
                                                            <td className={cellClass}>{v.code ?? naLabel}</td>
                                                            <td className={cellClass}>{v.recipientEmail ?? naLabel}</td>
                                                            <td className={cellClass}>{`${v.usedCounter} / ${v.maxLimit}`}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
            <CustomModal
                open={!!cancelling}
                title={t('vouchers_codes.campaign.cancel.title')}
                onClose={() => setCancelling(null)}
                closeButton={
                    <CustomButton
                        title={t('vouchers_codes.cancel_code.keep')}
                        isFilled={false}
                        onClick={() => setCancelling(null)}
                    />
                }
                saveButton={
                    <CustomButton
                        title={t('vouchers_codes.campaign.cancel.confirm')}
                        loading={isCancelling}
                        disabled={isCancelling}
                        onClick={handleConfirmCancel}
                    />
                }
            >
                <div className={'flex flex-col gap-3'}>
                    <p className={'text-sm text-foreground/80'}>
                        {t('vouchers_codes.campaign.cancel.message', {
                            name: cancelling?.name ?? '',
                            count: cancelling?.issuedCount ?? 0,
                        })}
                    </p>
                    {cancelError && <p className={'text-sm text-red-500'}>{cancelError}</p>}
                </div>
            </CustomModal>
        </>
    );
};

export default CampaignsTable;
